import * as React from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '../lib/utils';
import { repairMarkdown } from '../lib/repair-markdown';
import { ALERT_KINDS, rehypeGfmAlerts, type AlertKind } from '../lib/rehype-gfm-alerts';
import { rehypeQuotedSpeech } from '../lib/rehype-quoted-speech';

const CodeBlockView = React.lazy(() =>
  import('./viewers/CodeBlockView').then((m) => ({ default: m.CodeBlockView })),
);

export interface MarkdownContentProps {
  content: string;
  className?: string;
  /** The content is still arriving; repair half-typed syntax instead of marking it. */
  streaming?: boolean;
}

interface HastLike {
  type?: string;
  tagName?: string;
  properties?: Record<string, unknown>;
  children?: HastLike[];
}

const ALERT_TITLES: Record<AlertKind, string> = {
  note: 'Note',
  tip: 'Tip',
  important: 'Important',
  warning: 'Warning',
  caution: 'Caution',
};

const ALERT_CLASS: Record<AlertKind, string> = {
  note: 'md-alert md-alert-note',
  tip: 'md-alert md-alert-tip',
  important: 'md-alert md-alert-important',
  warning: 'md-alert md-alert-warning',
  caution: 'md-alert md-alert-caution',
};

const ALERT_ICON_PATHS: Record<AlertKind, string> = {
  note: 'M12 16v-4m0-4h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z',
  tip: 'M9 18h6m-5 4h4M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2Z',
  important: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Zm-9-8v4m0 4h.01',
  warning: 'M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4m0 4h.01',
  caution: 'M7.9 2h8.2L22 7.9v8.2L16.1 22H7.9L2 16.1V7.9ZM12 8v4m0 4h.01',
};

function nodeText(node: React.ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return nodeText(node.props.children);
  return '';
}

function isAlertKind(value: unknown): value is AlertKind {
  return typeof value === 'string' && (ALERT_KINDS as readonly string[]).includes(value);
}

function hasClass(node: HastLike | undefined, name: string): boolean {
  const cls = node?.properties?.className;
  return Array.isArray(cls) && cls.includes(name);
}

function taskChecked(node: HastLike | undefined): boolean | null {
  const input = node?.children?.find((c) => c.type === 'element' && c.tagName === 'input');
  if (!input) return null;
  return input.properties?.checked === true;
}

function CodeBlock({ children, unclosed }: { children: React.ReactNode; unclosed: boolean }) {
  const [copied, setCopied] = React.useState(false);
  const code = React.Children.toArray(children).find(React.isValidElement) as
    | React.ReactElement<{ className?: string; children?: React.ReactNode }>
    | undefined;
  const language = code?.props.className?.match(/language-([\w+-]+)/)?.[1];
  const text = nodeText(code?.props.children ?? children).replace(/\n$/, '');

  React.useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  return (
    <div
      className="md-code-block"
      data-testid="markdown-code-block"
      data-unclosed={unclosed ? 'true' : undefined}
    >
      <div className="md-code-block-header">
        <span>
          <span className="md-code-block-label">{language ?? 'text'}</span>
          {unclosed && (
            <span
              data-testid="markdown-code-unclosed"
              title="The reply ended without closing this code block"
              className="md-code-block-unclosed"
            >
              unclosed
            </span>
          )}
        </span>
        <button
          type="button"
          aria-label={copied ? 'Copied' : 'Copy code'}
          data-testid="markdown-code-copy"
          onClick={() => {
            void navigator.clipboard.writeText(text).then(() => setCopied(true));
          }}
          className="md-code-block-copy transition-colors"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <div className="md-code-block-body">
        <React.Suspense
          fallback={<pre className="overflow-x-auto font-mono text-[0.85em] leading-[1.55] text-foreground">{text}</pre>}
        >
          <CodeBlockView code={text} language={language} />
        </React.Suspense>
      </div>
    </div>
  );
}

function TableWrap({ children }: { children: React.ReactNode }) {
  const ref = React.useRef<HTMLDivElement | null>(null);
  const [overflow, setOverflow] = React.useState({ left: false, right: false });

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setOverflow((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      observer.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, []);

  return (
    <div
      ref={ref}
      className="md-table-wrap"
      data-testid="markdown-table-wrap"
      data-overflow-left={overflow.left ? 'true' : undefined}
      data-overflow-right={overflow.right ? 'true' : undefined}
    >
      <table className="md-table">{children}</table>
    </div>
  );
}

function AlertIcon({ kind }: { kind: AlertKind }) {
  return (
    <svg
      aria-hidden="true"
      width="1em"
      height="1em"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={ALERT_ICON_PATHS[kind]} />
    </svg>
  );
}

function isExternal(href: string | undefined): boolean {
  return href !== undefined && /^https?:\/\//.test(href);
}

export const MarkdownContent = React.memo(function MarkdownContent({
  content,
  className,
  streaming = false,
}: MarkdownContentProps) {
  const repaired = React.useMemo(() => repairMarkdown(content, { streaming }), [content, streaming]);

  const components = React.useMemo<Components>(
    () => ({
      h1: ({ children }) => <h1 className="md-h1">{children}</h1>,
      h2: ({ children, id }) =>
        id === 'footnote-label' ? (
          <h2 id={id} className="md-footnotes-title">
            {children}
          </h2>
        ) : (
          <h2 className="md-h2">{children}</h2>
        ),
      h3: ({ children }) => <h3 className="md-h3">{children}</h3>,
      h4: ({ children }) => <h4 className="md-h4">{children}</h4>,
      h5: ({ children }) => <h5 className="md-h5">{children}</h5>,
      h6: ({ children }) => <h6 className="md-h6">{children}</h6>,
      p: ({ children }) => <p className="md-p">{children}</p>,
      strong: ({ children }) => <strong className="md-strong">{children}</strong>,
      em: ({ children }) => <em className="md-em">{children}</em>,
      del: ({ children }) => <del className="md-del">{children}</del>,
      ul: ({ children, node }) => (
        <ul className={cn('md-list', hasClass(node, 'contains-task-list') ? 'md-task-list' : 'md-ul')}>
          {children}
        </ul>
      ),
      ol: ({ children, start }) => (
        <ol className="md-list md-ol" start={start}>
          {children}
        </ol>
      ),
      li: ({ children, node, id }) => {
        const checked = taskChecked(node);
        if (checked === null) {
          return (
            <li id={id} className="md-li">
              {children}
            </li>
          );
        }
        return (
          <li className="md-li md-task-item" data-checked={checked ? 'true' : 'false'}>
            {children}
          </li>
        );
      },
      input: ({ type, checked }) =>
        type === 'checkbox' ? (
          <input className="md-task-checkbox" type="checkbox" checked={checked === true} disabled readOnly />
        ) : null,
      blockquote: ({ children, node }) => {
        const kind = node?.properties?.dataAlert;
        if (isAlertKind(kind)) {
          return (
            <div className={ALERT_CLASS[kind]} data-testid="markdown-alert" data-alert={kind} role="note">
              <p className="md-alert-title">
                <AlertIcon kind={kind} />
                {ALERT_TITLES[kind]}
              </p>
              {children}
            </div>
          );
        }
        return <blockquote className="md-blockquote">{children}</blockquote>;
      },
      hr: () => <hr className="md-hr" />,
      pre: ({ children, node }) => (
        <CodeBlock unclosed={repaired.unclosedFence && node?.position?.end.offset === repaired.text.length}>
          {children}
        </CodeBlock>
      ),
      code: ({ children, className: codeClassName }) => {
        const isBlock = codeClassName?.startsWith('language-') || nodeText(children).includes('\n');
        if (isBlock) return <code className={codeClassName}>{children}</code>;
        return <code className="md-code-inline">{children}</code>;
      },
      a: ({ children, href, id, node }) => {
        if (node?.properties?.dataFootnoteRef !== undefined) {
          return (
            <a id={id} href={href} className="md-link" data-footnote-ref="">
              [{children}]
            </a>
          );
        }
        if (node?.properties?.dataFootnoteBackref !== undefined) {
          return (
            <a href={href} className="md-link" aria-label="Back to reference">
              {children}
            </a>
          );
        }
        const external = isExternal(href);
        const auto = nodeText(children) === href || `mailto:${nodeText(children)}` === href;
        return (
          <a
            href={href}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener noreferrer' : undefined}
            className={cn('md-link', auto && 'md-autolink')}
          >
            {children}
            {external && !auto && (
              <span aria-hidden="true" className="md-link-external">
                ↗
              </span>
            )}
          </a>
        );
      },
      sup: ({ children }) => <sup className="md-footnote-ref">{children}</sup>,
      section: ({ children, node }) =>
        node?.properties?.dataFootnotes !== undefined ? (
          <section className="md-footnotes" data-testid="markdown-footnotes">
            {children}
          </section>
        ) : (
          <section>{children}</section>
        ),
      table: ({ children }) => <TableWrap>{children}</TableWrap>,
      tbody: ({ children }) => <tbody className="md-tbody">{children}</tbody>,
      tr: ({ children }) => <tr className="md-tr">{children}</tr>,
      th: ({ children, style }) => (
        <th className="md-th" style={style}>
          {children}
        </th>
      ),
      td: ({ children, style }) => (
        <td className="md-td" style={style}>
          {children}
        </td>
      ),
      img: ({ src, alt }) => (
        <span className="md-figure">
          <img src={src} alt={alt ?? ''} className="md-img" />
          {alt ? <span className="md-img-caption">{alt}</span> : null}
        </span>
      ),
    }),
    [repaired],
  );

  return (
    <div className={cn('md-root text-body-reading text-copy', className)} data-testid="markdown-content">
      <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeGfmAlerts, rehypeQuotedSpeech]} components={components}>
        {repaired.text}
      </ReactMarkdown>
    </div>
  );
});
